import { createContext, useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import type { AnchorFABAction, FABContextValue } from './types'

const STORAGE_KEY = 'anchor_fab_seen'

export const FABContext = createContext<FABContextValue | null>(null)

interface FABProviderProps {
  children: ReactNode
}

export function FABProvider({ children }: FABProviderProps) {
  const [actions, setActionsState] = useState<Array<AnchorFABAction>>([])
  const [badge, setBadgeState] = useState(false)
  const [pulse, setPulseState] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  const setActions = useCallback((newActions: Array<AnchorFABAction>) => {
    setActionsState(newActions)
  }, [])

  const setBadge = useCallback((show: boolean) => {
    setBadgeState(show)
  }, [])

  const setPulse = useCallback((show: boolean) => {
    setPulseState(show)
  }, [])

  const openMenu = useCallback(() => {
    setIsOpen(true)
    // Mark as seen on first open
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, 'true')
    }
  }, [])

  const closeMenu = useCallback(() => {
    setIsOpen(false)
  }, [])

  const value = useMemo<FABContextValue>(
    () => ({
      actions,
      setActions,
      badge,
      setBadge,
      pulse,
      setPulse,
      isOpen,
      openMenu,
      closeMenu,
    }),
    [actions, setActions, badge, setBadge, pulse, setPulse, isOpen, openMenu, closeMenu]
  )

  return <FABContext.Provider value={value}>{children}</FABContext.Provider>
}

export function hasSeenFAB(): boolean {
  if (typeof window === 'undefined') return true
  return localStorage.getItem(STORAGE_KEY) === 'true'
}
